import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core/internal";
import { ConsentService, I18nType, LogService } from "../../services";
import { ConsentType, ConsentUiElement } from "../../services/consent/consent-type";
import { replacePlaceholderWithCustomFunctionButton, replaceHtmlLinks, replaceAllPlaceholdersWithText, LinkContainer, LinkTarget } from "../../utils";
import { ViewState, ViewStateType } from "../dock-privacy-settings/view-state";
import emptyLocale from "./empty-locale.json";
import availableLocales from "../../../tmp/availableLocales.json";
import deepmerge from "deepmerge";

@Component({
	tag: "modal-view",
	styleUrl: "modal-view.scss",
	shadow: false,
})
export class ModalView {
	/**
	 * Title text
	 */
	@State() uiTextContent: I18nType = emptyLocale;

	/**
	 * Object that holds internal data for category links.
	 * For structure example see corresponding prop.
	 * Dynamic access works via bracket syntax, though: uiCategoryLinksData[categoryName]
	 */
	@State() uiCategoryLinksData: LinkContainer = {};

	/**
	 * Object that holds internal data for footer links.
	 */
	@State() uiFooterLinksData: LinkContainer = {};

	/**
	 * More info privacy settings text in initial view
	 */
	@State() uiTextMoreInfoPrivacySettings: any[] = [];

	/**
	 * Third party and Eu description text in initial view
	 */
	@State() uiTextThirdPartyAndEuDesc: string = "";

	/**
	 * List of all checkbox elements
	 */
	@State() uiCheckboxList: ConsentUiElement[] = [];

	/**
	 * HTML class that is used to determine, whether buttons should have an icon
	 */
	@State() iconClass: string = "modal-icons";

	/**
	 * Disables the language selector
	 */
	@Prop({ reflect: true }) disableLanguageSelector: boolean = false;

	/**
	 * Disables DOCK's icons
	 */
	@Prop({ reflect: true }) disableIcons: boolean = false;

	/**
	 * The object containing all the texts (and other content) for modal view
	 * We're assigning "emptyLocale" so we already have the correct structure with empty strings, to avoid "undefined" errors (mainly in tests).
	 */
	@Prop({ mutable: false }) localizationContent: I18nType = emptyLocale;

	/**
	 * Sets visibility of modal
	 */
	@Prop({ mutable: true }) show: boolean = false;

	/**
	 * Sets the defined view (e.g. initial view, edit view) to render
	 */
	@Prop() viewState: ViewStateType = ViewState.InitialView;

	// TODO: Remove unused props etc.!!!
	/**
	 * List of all consent categories
	 */
	@Prop() uiConsentCategoryList: string[] = [];

	/**
	 * Defines whether the "Decline All" button will be displayed
	 */
	@Prop({ reflect: true }) disableDeclineAll: boolean;

	/**
	 * Defines whether the preventAutoOpen parameter will be added to built-in links
	 */
	@Prop() preventAutoOpen: boolean = false;

	/**
	 * Prevent auto open url parameter text
	 */
	@Prop() preventAutoOpenUrlParameter: string;

	/**
	 * Is third party data processing active?
	 */
	@Prop() dataProcessingThirdParty: boolean;

	/**
	 * Is data processing outside EU active?
	 */
	@Prop() dataProcessingOutsideEea: boolean;

	/**
	 * Object that holds (user) consent data
	 */
	@Prop() consentData: ConsentType[];

	/**
	 * Data for category links (LinkContainer)
	 * Structure example:
	 * categoryLinksData = {
	 *     convenience: {
	 *         name: "convenience",
	 *         linkUrl: "/cookies#convenience",
	 *         linkLabel: "Convenience"		//NOTE: linkLabel is currently not passed on will be replaced by text from i18n!
	 *     },
	 *     marketing: {
	 *         name: "marketing",
	 *         linkUrl: "http:foo.bar/cookies#marketing",
	 *         linkLabel: "Marketing and stuff"
	 *     }
	 * }
	 */
	@Prop() categoryLinksData: LinkContainer = {};

	/**
	 *  Sets the link address (href) of the more info link
	 */
	@Prop() linkUrlMoreInfo: string = undefined;

	/**
	 * Data for footer links (LinkContainer), structure same as categoryLinksData
	 */
	@Prop() footerLinksData: LinkContainer = {};

	/**
	 * Controls if the links are going to open in a new tab
	 */
	@Prop() linksTarget: string = LinkTarget.SELF;

	/**
	 * A flag used to enable the alternative DOCK template
	 */
	@Prop() enableAlternativeEditView: boolean = false;

	/**
	 * Sets the link address (href) of the email link
	 */
	@Prop() linkUrlEmail: string = undefined;

	/**
	 * Disable the initial view of DOCK
	 */
	@Prop({ reflect: true }) disableInitialView: boolean = false;

	/**
	 * Event emitter for opened modal
	 */
	@Event() modalShowEvent: EventEmitter;

	/**
	 * Event emitter for closed modal
	 */
	@Event() modalHideEvent: EventEmitter;

	/**
	 * Event emitter for 'accept all' button
	 */
	@Event() saveAcceptAllEvent: EventEmitter;

	/**
	 * Event emitter for 'decline all' button
	 */
	@Event() saveDeclineAllEvent: EventEmitter;

	/**
	 * Event emitter for 'save' button
	 */
	@Event() saveCurrentSelectionEvent: EventEmitter;

	/**
	 * Event emitter for language update from language selector
	 */
	@Event() localeChangeEvent: EventEmitter;

	/**
	 * Event emitter for modal checkbox toggle
	 */
	@Event() checkboxToggleModalEvent: EventEmitter<ConsentType>;

	/**
	 * Event emitter for change of modal view state
	 */
	@Event() modalViewStateChangedEvent: EventEmitter<ViewStateType>;

	/**
	 * Event emitter for change to edit view from privacy settings link
	 */
	@Event() changeToEditViewEvent: EventEmitter;

	/**
	 * Event emitter for change to initial view from privacy settings link
	 */
	@Event() changeToInitialViewEvent: EventEmitter;

	/**
	 * The 'modal-view' HTML element
	 */
	@Element() hostElement: HTMLModalViewElement;

	/**
	 * Quick fix for language duplication
	 */
	private localesSet: boolean = false;

	constructor() {
		this.handleCheckbox = this.handleCheckbox.bind(this);
		this.changeToEditView = this.changeToEditView.bind(this);
	}

	@Watch("localizationContent")
	uiTextContentWatcher(textContent) {
		LogService.log.debug("Updated textContent:", textContent);
	}

	/**
	 * Watches the 'enableAlternativeView' attribute for value changes
	 * @param newValue
	 */
	@Watch("enableAlternativeEditView")
	async toggleAlternativeView(newValue: boolean): Promise<void> {
		LogService.log.info("Alternative view is set to:", newValue);
	}

	/**
	 * Watches the show attribute for value changes and emits an event
	 * @param isVisible
	 */
	@Watch("show")
	modalVisibility(isVisible: boolean): void {
		if (isVisible) {
			this.modalShowEvent.emit();
		} else {
			this.modalHideEvent.emit();
		}
	}

	@Watch("viewState")
	changedModalViewState(viewState: ViewStateType): void {
		this.modalViewStateChangedEvent.emit(viewState);
	}

	/**
	 * Watches the disable-decline-all parameter for changes
	 */
	@Watch("disableDeclineAll")
	updateDisableDeclineAll(): void {
		LogService.log.debug("Visibility of decline all button is now set to: ", this.disableDeclineAll);
	}

	/**
	 * Watches for changes of the prevent-auto-open url parameter
	 */
	@Watch("preventAutoOpenUrlParameter")
	updatePreventAutoOpenUrlParameter(): void {
		LogService.log.debug("Prevent auto open parameter has been changed to: ", this.preventAutoOpenUrlParameter);
	}

	/**
	 * Watches for changes of the prevent-auto-open parameter
	 */
	@Watch("preventAutoOpen")
	updatePreventAutoOpen(newValue: boolean): void {
		LogService.log.debug("Prevent auto open has been set to:", newValue);
	}

	// //TODO: Currently not used! Use or delete!
	// validatelinkTarget(target: LinkTarget) {
	// 	if (target !== "_self" && target !== "_blank") {
	// 		throw new Error("Invalid link target value: " + target);
	// 	}
	// }

	async componentWillRender() {
		// NOTE: The following order is important, so e.g. footer links and checkbox labels are built correctly!
		if (this.localizationContent) {
			// Create a deep copy of localization content. We use deepmerge here, as we already have it as a dependency.
			// There is a native JS solution: https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
			// However, this is relatively new and should not be used here, yet! (Re-evaluate in 2024, also if deepmerge is still needed)
			this.uiTextContent = deepmerge(emptyLocale, this.localizationContent);
			await this.applyLocale(this.uiTextContent);
		}

		if (
			this.enableAlternativeEditView &&
			this.uiTextContent.alternativeEditView.paragraph1.toString().length === 0 &&
			this.uiTextContent.alternativeEditView.paragraph4.toString().length === 0
		) {
			this.show = false;
			LogService.log.warn(
				"It seems that texts for alternative edit view are not defined. DOCK will not be displayed. Please configure texts in i18nOverride, use locale with defined texts or do not use alternative-edit-view!",
			);
		}

		this.uiCategoryLinksData = this.applyAllLinks(this.categoryLinksData, "purposeCategories");
		this.uiFooterLinksData = this.applyAllLinks(this.footerLinksData, "footer");

		if (this.consentData) {
			await this.applyConsents(this.consentData);
		}
		if (this.disableIcons) {
			this.iconClass = "";
		}
	}

	render() {
		if (this.viewState === "initial") {
			if (!this.disableInitialView) {
				return this.createInitialView();
			}
		} else {
			return this.enableAlternativeEditView === false ? this.createEditView() : this.createAlternativeEditView();
		}
	}

	componentDidLoad() {
		this.modalShowEvent.emit();
	}

	/**
	 * Applies the locale data texts to the according UI elements
	 * @param content
	 */
	private async applyLocale(content: I18nType): Promise<void> {
		LogService.log.debug("Apply locale");

		this.uiTextContent = replaceAllPlaceholdersWithText(content, [content.general, content.initialView, content.editView]);
		LogService.log.trace("uiTextContent after replacements", this.uiTextContent);

		switch (this.viewState) {
			case ViewState.InitialView:
				this.applyInitialViewTexts(this.uiTextContent);
				break;
			case ViewState.EditView:
				this.applyEditViewTexts(this.uiTextContent);
				break;
		}

		LogService.log.debug("Applied language to user interface");
	}

	private applyInitialViewTexts(content: I18nType) {
		LogService.log.debug("Apply initial view texts");

		this.uiTextThirdPartyAndEuDesc = content.initialView.thirdPartyAndEuDesc;

		this.uiTextMoreInfoPrivacySettings = replacePlaceholderWithCustomFunctionButton(
			`${content.initialView.moreInfoPrivacySettings} ${content.initialView.consentWithdrawInfo}`,
			//TODO: Check if %SETTINGS_LINK% is in some / all of the language files !!!
			"%SETTINGS_LINK%",
			content.general.options,
			this.changeToEditView,
		);

		this.uiTextThirdPartyAndEuDesc = this.createDataProcessingText(
			this.uiTextThirdPartyAndEuDesc,
			content.initialView.byThirdPartiesFragment,
			content.initialView.outsideEuFragment,
			content.general.and,
		);
	}

	private createDataProcessingText(uiTextThirdPartyAndEuDesc, byThirdPartiesFragment, outsideEeaFragment, andFragment) {
		if (!this.dataProcessingThirdParty && !this.dataProcessingOutsideEea) {
			return undefined;
		}

		let tempTextThirdPartyAndEuDesc = uiTextThirdPartyAndEuDesc;

		let byThirdPartyReplaceText = this.dataProcessingThirdParty ? byThirdPartiesFragment : "";
		tempTextThirdPartyAndEuDesc = tempTextThirdPartyAndEuDesc.replace(`[${this.uiTextContent.initialView.byThirdPartiesFragment}]`, byThirdPartyReplaceText);

		let andReplaceText = this.dataProcessingOutsideEea && this.dataProcessingThirdParty ? ` ${andFragment} ` : "";
		tempTextThirdPartyAndEuDesc = tempTextThirdPartyAndEuDesc.replace(` [${this.uiTextContent.general.and}] `, andReplaceText);

		let outsideEeaReplaceText = this.dataProcessingOutsideEea ? outsideEeaFragment : "";
		tempTextThirdPartyAndEuDesc = tempTextThirdPartyAndEuDesc.replace(`[${this.uiTextContent.initialView.outsideEuFragment}]`, outsideEeaReplaceText);

		return tempTextThirdPartyAndEuDesc;
	}

	private applyEditViewTexts(content: I18nType) {
		LogService.log.debug("Apply edit view texts", this.dataProcessingThirdParty);
		let updatedUrl: string = "";

		if (this.linkUrlMoreInfo) {
			updatedUrl = this.preventAutoOpen ? this.applyPreventAutoOpenParam(this.linkUrlMoreInfo) : this.linkUrlMoreInfo;
		}

		// ToDo: LinkData is just "parked" here for now. This should be something dynamic and generally available!
		const linkData = [
			{
				linkUrl: updatedUrl,
				linkLabel: content.general.moreInfo,
				linkTarget: this.linksTarget,
				linkPlaceholder: "%MORE_INFO_LINK%",
			},
			{
				linkUrl: this.linkUrlEmail,
				linkLabel: this.uiTextContent.general.emailLink,
				linkTarget: this.linksTarget,
				linkPlaceholder: "%EMAIL_LINK%",
			},
		];

		// Apply "third party" fragment to Edit View - Extended data processing purpose list description
		let andThirdPartyReplaceText = this.dataProcessingThirdParty ? content.editView.andThirdPartiesFragment : "";
		this.uiTextContent.editView.extDataProcessingPurposeListDesc = (this.uiTextContent.editView.extDataProcessingPurposeListDesc as string).replace(
			`[${content.editView.andThirdPartiesFragment}]`,
			andThirdPartyReplaceText,
		);

		this.uiTextContent.editView.extDataProcessingPurposeListDesc = replaceHtmlLinks(this.uiTextContent.editView.extDataProcessingPurposeListDesc as string, linkData);

		this.uiTextContent.alternativeEditView.paragraph1 = replaceHtmlLinks(this.uiTextContent.alternativeEditView.paragraph1 as string, linkData);
		this.uiTextContent.alternativeEditView.paragraph2 = replaceHtmlLinks(this.uiTextContent.alternativeEditView.paragraph2 as string, linkData);
		this.uiTextContent.alternativeEditView.paragraph3 = replaceHtmlLinks(this.uiTextContent.alternativeEditView.paragraph3 as string, linkData);
		this.uiTextContent.alternativeEditView.paragraph4 = replaceHtmlLinks(this.uiTextContent.alternativeEditView.paragraph4 as string, linkData);
		this.uiTextContent.alternativeEditView.paragraph5 = replaceHtmlLinks(this.uiTextContent.alternativeEditView.paragraph5 as string, linkData);
		this.uiTextContent.alternativeEditView.paragraph6 = replaceHtmlLinks(this.uiTextContent.alternativeEditView.paragraph6 as string, linkData);
	}

	//ToDo: Move link stuff into separate service!
	private applyAllLinks(linkContainer: LinkContainer, linkType: "footer" | "purposeCategories") {
		LogService.log.debug("Applying links:", linkContainer);

		let tempLinks = {};
		const textObject = this.uiTextContent[linkType];

		for (let [categoryname, linkData] of Object.entries(linkContainer)) {
			let tempUrl = "";
			// first assign value from link config
			if (linkData.linkUrl !== "") {
				tempUrl = linkData.linkUrl;
			}
			// then assign linkUrl from i18n if it exists
			if (textObject[categoryname].linkUrl !== "") {
				tempUrl = textObject[categoryname].linkUrl;
			}
			if (tempUrl === "") {
				LogService.log.warn(`No link URL configured for category '${categoryname}'! Consider deactivating the category or configuring a link!`);
			}

			tempUrl = this.preventAutoOpen ? this.applyPreventAutoOpenParam(tempUrl) : tempUrl;
			tempLinks[categoryname] = { name: categoryname, linkLabel: textObject[categoryname].linkLabel, linkUrl: tempUrl, linkTarget: this.linksTarget };
		}

		LogService.log.debug("Result link array: ", tempLinks);
		return tempLinks as LinkContainer;
	}

	/**
	 * We are splitting the hash values from the rest of the URL, so we can insert the parameter string prevent-auto-open-privacy-settings before them. After that we are checking if we have other parameters and if we do we insert prevent-auto-open-privacy-settings with &, if we don't we insert it with ? as a first parameter. After the parameter is inserted the hash values are concatenated back to the url.
	 */
	private applyPreventAutoOpenParam(inputLink: string) {
		// ToDo: Review this implementation to use URLSearchParams later (2025?)
		// Currently, either only absolute links could be returned or - if it shall be differentiated between absolute and relative URLs,
		// nested try-catch blocks would have to be used with new URL(input Link) (see commit af4d1c23bada05b0a3cfe218f431d05fed5ad3a3)
		// There is URL.canParse, which could simplify the implementation but it's not widely supported, yet.

		if (inputLink) {
			const key: string = this.preventAutoOpenUrlParameter;
			const filtered = inputLink.split("#");
			const re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
			const separator = filtered[0].includes("?") ? "&" : "?";
			let hashValue: string = "";

			if (filtered.length >= 2) {
				// eslint-disable-next-line sonarjs/no-ignored-return
				filtered.splice(1).map(element => (hashValue = hashValue + "#" + element));
			}

			return re.test(filtered[0]) ? filtered[0].replace(re, "$1" + key + "$2") + hashValue : filtered[0] + separator + key + hashValue;
		}
		return undefined;
	}

	private async applyConsents(consents: ConsentType[]): Promise<void> {
		LogService.log.debug("Applying consent categories", consents);
		LogService.log.trace("...based on uiConsentCategoryList", this.uiConsentCategoryList);
		let standardConsents: ConsentUiElement[] = [];
		let customConsents: ConsentUiElement[] = [];

		for (const category of this.uiConsentCategoryList) {
			if (category.length > 0) {
				const label = this.uiTextContent.purposeCategories[category].nameLong;

				const descriptionText = this.uiTextContent.purposeCategories[category].descriptionList;

				let userConsents = false;
				// pick userConsent from consents array which matches the category name
				if (consents.some(element => element.name === category)) {
					userConsents = consents.find(element => element.name === category).userConsents;
				}

				standardConsents.push(
					...this.myConsentUiElementFactory([
						{
							checkboxId: category,
							name: category,
							label: label,
							linkData: this.uiCategoryLinksData[category],
							userConsents: userConsents,
							descriptionText: descriptionText,
						},
					]),
				);
			}
		}

		this.uiCheckboxList = [...standardConsents, ...customConsents];
		LogService.log.trace("Done with consent categories", this.uiCheckboxList);
	}

	private checkConsent(value: string) {
		return this.uiConsentCategoryList.find(element => element === value);
	}
	// private async applyConsents(consents: ConsentType[]): Promise<void> {
	// 	LogService.log.debug("Applying consent categories", consents);
	// 	let standardConsents: ConsentUiElement[] = [];
	// 	let customConsents: ConsentUiElement[] = [];
	//
	// 	for (const category of this.uiConsentCategoryList) {
	// 		const label = this.uiTextContent.purposeCategories[category].nameLong;
	//
	// 		const descriptionText = this.uiTextContent.purposeCategories[category].descriptionList;
	//
	// 		let userConsents = false;
	// 		// pick userConsent from consents array which matches the category name
	// 		if (consents.some(element => element.name === category)) {
	// 			userConsents = consents.find(element => element.name === category).userConsents;
	// 		}
	//
	// 		standardConsents.push(
	// 			...this.myConsentUiElementFactory([
	// 				{
	// 					checkboxId: category,
	// 					name: category,
	// 					label: label,
	// 					linkData: this.uiCategoryLinksData[category],
	// 					userConsents: userConsents,
	// 					descriptionText: descriptionText,
	// 				},
	// 			]),
	// 		);
	// 	}
	//
	// 	this.uiCheckboxList = [...standardConsents, ...customConsents];
	// 	LogService.log.debug("Done with consent categories", this.uiCheckboxList);
	// }

	// private createUiConsentCategoryList() {
	// 	let uiConsentCategoryList = [];
	//
	// 	if (!this.disabledConvenience) {
	// 		uiConsentCategoryList.push(StandardConsent.Convenience);
	// 	}
	// 	if (!this.disabledAnalysis) {
	// 		uiConsentCategoryList.push(StandardConsent.Analysis);
	// 	}
	// 	if (!this.disabledMarketing) {
	// 		uiConsentCategoryList.push(StandardConsent.Marketing);
	// 	}
	// 	return uiConsentCategoryList;
	// }

	// Copied from consent-service for now
	// Maybe this whole thing is redundant!?!?!?!?!?
	/**
	 * ConsentUiElement factory that creates an array of consents with index numbers to be used in user interface code
	 * @param consentUiValues
	 */
	private myConsentUiElementFactory(consentUiValues: ConsentUiElement[]): ConsentUiElement[] {
		let uiConsents: ConsentUiElement[] = [];
		for (const consentUiValue of consentUiValues) {
			uiConsents.push({
				...ConsentService.consentFactory(consentUiValue.name, consentUiValue.userConsents),
				label: consentUiValue.label,
				checkboxId: consentUiValue.checkboxId.toString(),
				linkData: consentUiValue.linkData,
				descriptionText: consentUiValue.descriptionText,
			});
		}

		return uiConsents;
	}

	private createInitialView() {
		LogService.log.debug("Create Initial View");
		this.localesSet = false;
		let description = (
			<div>
				<p class="first-p">
					<div class="desc1">
						<span id="data-processing-reason">{this.uiTextContent.initialView.dataProcessingReason}</span>&nbsp;
						<span id="data-processing-desc">{this.uiTextContent.initialView.dataProcessingDesc} </span>
						{this.checkConsent("convenience") && (
							<span id="convenience-desc-clause">
								<span class="text-bold">{this.uiTextContent.purposeCategories.convenience.descriptionClause}</span> (
								{this.uiTextContent.purposeCategories.convenience.name})
								{this.checkConsent("analysis") || this.checkConsent("marketing") || this.checkConsent("advertising") ? <span>,&nbsp;</span> : <span>.&nbsp;</span>}
							</span>
						)}
						{this.checkConsent("analysis") && (
							<span id="analysis-desc-clause">
								<span class="text-bold">{this.uiTextContent.purposeCategories.analysis.descriptionClause}</span> (
								{this.uiTextContent.purposeCategories.analysis.name})
								{this.checkConsent("marketing") || this.checkConsent("advertising") ? <span>,&nbsp;</span> : <span>.&nbsp;</span>}
							</span>
						)}
						{this.checkConsent("marketing") && (
							<span id="marketing-desc-clause">
								<span class="text-bold">{this.uiTextContent.purposeCategories.marketing.descriptionClause}</span> (
								{this.uiTextContent.purposeCategories.marketing.name})
								{this.checkConsent("advertising") && this.uiTextContent.purposeCategories.advertising.name ? <span>,&nbsp;</span> : <span>.&nbsp;</span>}
							</span>
						)}
						{this.checkConsent("advertising") && (
							<span id="advertising-desc-clause">
								<span class="text-bold">{this.uiTextContent.purposeCategories.advertising.descriptionClause}</span>
								{this.uiTextContent.purposeCategories.advertising.name ? <span> ({this.uiTextContent.purposeCategories.advertising.name}).</span> : ""}
							</span>
						)}
					</div>
				</p>
				{this.uiTextThirdPartyAndEuDesc && (
					<p class="second-p">
						<div class="desc2" id="third-party-and-eu">
							{this.uiTextThirdPartyAndEuDesc}
						</div>
					</p>
				)}
				<p class="third-p">
					<div class="desc2" id="more-info-privacy">
						{this.uiTextMoreInfoPrivacySettings}
					</div>
				</p>
			</div>
		);
		let saveAllButton = this.createSaveAllButton();
		let declineButton = this.disableDeclineAll ? "" : this.createDeclineAllButton();
		let options = this.createOptionsButton();
		let footer = this.createFooterLinks();

		return (
			<Host key="initial">
				<bbg-dialog key="initial" type="modal" show={this.show}>
					<span slot="headline" id="title">
						{this.capitalizeFirstLetter(this.uiTextContent.initialView.headline)}
					</span>
					<span slot="content">{description}</span>
					<span key="initial" style={{ display: "block" }} slot="actions">
						<span class="button-group " style={{ "margin-top": "0" }}>
							{saveAllButton}
							{declineButton}
							{options}
						</span>
						<div class="footer">{footer}</div>
					</span>
				</bbg-dialog>
			</Host>
		);
	}

	private createEditView() {
		LogService.log.debug("Create Edit View");

		let checkboxList = this.createCheckboxList();
		let description = (
			<div>
				<p class="first-p">
					<div class="desc1">
						<span id="basic-data-usage-desc">{this.uiTextContent.editView.basicDataUsageDesc}</span>
					</div>
				</p>
				<p class="second-p">
					<span id="ext-data-processing-purpose-list-desc">{this.uiTextContent.editView.extDataProcessingPurposeListDesc}</span>
				</p>
				{checkboxList}
				<p class="fourth-p">
					<span id="ext-consent-withdraw">{this.uiTextContent.editView.extConsentWithdraw}</span>&nbsp;
					<span id="privacy-settings-explainer">{this.uiTextContent.editView.privacySettingsExplainer}</span>
				</p>
			</div>
		);
		let saveButton = this.createSaveButton();
		let footer = this.createFooterLinks();
		let backButton = this.createBackButton();

		const localeList = [];
		for (const locale of availableLocales) {
			localeList.push({ locale: locale.locale, label: locale.label });
		}

		return (
			<Host key="initial">
				<bbg-dialog key="edit" type="modal" show={this.show}>
					<span slot="top-content" class="top-content">
						{!this.disableInitialView && backButton}
						{!this.disableLanguageSelector && this.createLanguageSelector(localeList)}
					</span>
					<span slot="headline" id="title">
						{this.capitalizeFirstLetter(this.uiTextContent.editView.headline)}
					</span>
					<span slot="content">{description}</span>

					<span key="initial" style={{ display: "block" }} slot="actions">
						<span class="button-group " style={{ "margin-top": "0" }}>
							{saveButton}
						</span>
						<div class="footer">{footer}</div>
					</span>
				</bbg-dialog>
			</Host>
		);
	}

	private createAlternativeEditView() {
		LogService.log.debug("Create alternative Edit View");

		let checkboxList = this.createCheckboxList();

		let description = (
			<div>
				<p id="edit-view-p1">
					<div class="desc1">
						<span id="first-paragraph">{this.uiTextContent.alternativeEditView.paragraph1}</span>
					</div>
				</p>
				<p id="edit-view-p2">
					<span id="second-paragraph">{this.uiTextContent.alternativeEditView.paragraph2}</span>
				</p>
				<p id="edit-view-p3">
					<span id="third-paragraph">{this.uiTextContent.alternativeEditView.paragraph3}</span>
				</p>
				{checkboxList}
				<p id="edit-view-p4">
					<span id="fourth-paragraph">{this.uiTextContent.alternativeEditView.paragraph4}</span>
				</p>
				<p id="edit-view-p5">
					<span id="fifth-paragraph">{this.uiTextContent.alternativeEditView.paragraph5}</span>
				</p>
				<p id="edit-view-p6">
					<span id="sixth-paragraph">{this.uiTextContent.alternativeEditView.paragraph6}</span>
				</p>
			</div>
		);
		let saveButton = this.createSaveButton();
		let footer = this.createFooterLinks();
		let backButton = this.createBackButton();

		return (
			<Host key="initial">
				<bbg-dialog key="edit" type="modal" show={this.show}>
					<span slot="top-content" class="top-content">
						{!this.disableInitialView && backButton}
					</span>
					<span slot="headline" id="title">
						{this.uiTextContent.alternativeEditView && this.capitalizeFirstLetter(this.uiTextContent.alternativeEditView.headline)}
					</span>
					<span slot="content">{description}</span>

					<span key="initial" style={{ display: "block" }} slot="actions">
						<span class="button-group " style={{ "margin-top": "0" }}>
							{saveButton}
						</span>
						<div class="footer">{footer}</div>
					</span>
				</bbg-dialog>
			</Host>
		);
	}

	private createBackButton() {
		let backButtonLabel = "< " + this.capitalizeFirstLetter(this.uiTextContent.general.back);
		return <bbg-button id="back-button" type="tertiary" label={backButtonLabel} onClick={() => this.changeToInitialView()}></bbg-button>;
	}

	private createOptionsButton() {
		return (
			<div class={"button-paragraph options-button " + this.iconClass}>
				<bbg-button
					id="options-modal-dialog"
					type="tertiary"
					label={this.capitalizeFirstLetter(this.uiTextContent.general.settings)}
					onClick={() => this.changeToEditView()}
				>
					<i class="a-icon a-button__icon">
						<svg id="options-icon" width="1.5em" viewBox="0 0 192 192" enable-background="new 0 0 512 512">
							<path d="M96 128.5c-17.9 0-32.5-14.6-32.5-32.5S78.1 63.5 96 63.5s32.5 14.6 32.5 32.5-14.6 32.5-32.5 32.5zm0-57c-13.5 0-24.5 11-24.5 24.5s11 24.5 24.5 24.5 24.5-11 24.5-24.5-11-24.5-24.5-24.5z" />
							<path d="M96 176c-4.2 0-8.4-.3-12.6-1l-2.6-.4-4.6-17.7c-3.1-1-6.2-2.3-9.1-3.8l-15.8 9.3-2.1-1.5A80 80 0 0 1 31.3 143l-1.5-2.1 9.3-15.8c-1.5-3-2.8-6-3.8-9.2l-17.7-4.6-.4-2.6c-.7-4.2-1-8.4-1-12.6s.3-8.4 1-12.6l.4-2.6 17.7-4.6c1-3.1 2.3-6.2 3.8-9.1l-9.3-15.8 1.5-2.1a80 80 0 0 1 17.9-17.9l2.1-1.5 15.7 9c3-1.5 6-2.8 9.2-3.8l4.6-17.7 2.6-.4c8.4-1.3 16.9-1.3 25.3 0l2.6.4 4.6 17.7c3.1 1 6.2 2.3 9.1 3.8l15.8-9.3 2.1 1.5A80 80 0 0 1 160.8 49l1.5 2.1-9.2 15.9c1.5 3 2.8 6 3.8 9.2l17.7 4.6.4 2.6c.7 4.2 1 8.4 1 12.6s-.3 8.4-1 12.6l-.4 2.6-17.7 4.6c-1 3.1-2.3 6.2-3.8 9.1l9.3 15.8-1.5 2.1a80 80 0 0 1-17.9 17.9l-2.1 1.5-15.8-9.3c-3 1.5-6 2.8-9.2 3.8l-4.6 17.7-2.6.4c-4.3.9-8.5 1.2-12.7 1.2zm-8.8-8.5c5.9.7 11.7.7 17.6 0l4.4-17 2.2-.6c4.1-1.2 8-2.8 11.7-4.9l2-1.1 15.2 8.9c4.6-3.6 8.8-7.8 12.5-12.5l-8.9-15.2 1.1-2c2.1-3.7 3.7-7.6 4.9-11.7l.6-2.2 17-4.4c.4-2.9.5-5.9.5-8.8s-.2-5.9-.5-8.8l-17-4.4-.6-2.2c-1.2-4.1-2.8-8-4.9-11.7l-1.1-2 8.9-15.2c-3.6-4.6-7.8-8.8-12.5-12.5l-15.2 8.9-2-1.1c-3.7-2.1-7.6-3.7-11.7-4.9l-2.2-.6-4.4-17c-5.9-.7-11.7-.7-17.6 0l-4.4 17-2.2.6c-4.1 1.2-8 2.8-11.7 4.9l-2 1.1-15.2-8.9c-4.6 3.6-8.8 7.8-12.5 12.5l8.9 15.2-1.1 2c-2.1 3.7-3.7 7.6-4.9 11.7l-.6 2.2-17 4.4c-.3 2.9-.5 5.9-.5 8.8s.2 5.9.5 8.8l17 4.4.6 2.2c1.2 4.1 2.8 8 4.9 11.7l1.1 2-8.9 15.2c3.6 4.6 7.8 8.8 12.5 12.5l15.2-8.9 2 1.1c3.7 2.1 7.6 3.7 11.7 4.9l2.2.6 4.4 17z" />
						</svg>
					</i>
				</bbg-button>
			</div>
		);
	}

	private createSaveAllButton() {
		return (
			<div class={this.iconClass}>
				<bbg-button
					id="save-all-modal-dialog"
					type="primary"
					label={this.capitalizeFirstLetter(this.uiTextContent.general.acceptAll)}
					onClick={() => this.saveAcceptAllAndCloseModal()}
				>
					<i class="a-icon a-button__icon">
						<svg id="accept-icon" width="1.5em" viewBox="0 0 192 192" enable-background="new 0 0 512 512">
							<path d="M76 141.7L33.2 98.8l5.6-5.6L76 130.3l77.2-77.1 5.6 5.6z" />
						</svg>
					</i>
				</bbg-button>
			</div>
		);
	}

	private createDeclineAllButton() {
		return (
			<div class={this.iconClass}>
				<bbg-button
					id="decline-all-modal-dialog"
					type="secondary"
					label={this.capitalizeFirstLetter(this.uiTextContent.general.declineAll)}
					onClick={() => this.saveDeclineAllAndCloseModal()}
				>
					<i class="a-icon a-button__icon">
						<svg id="decline-icon" width="1.5em" viewBox="0 0 192 192" enable-background="new 0 0 512 512">
							<path d="M158.8 38.8l-5.6-5.6L96 90.3 38.8 33.2l-5.6 5.6L90.3 96l-57.1 57.2 5.6 5.6L96 101.7l57.2 57.1 5.6-5.6L101.7 96z" />
						</svg>
					</i>
				</bbg-button>
			</div>
		);
	}

	private createSaveButton() {
		let buttonType: string = "primary";

		return (
			<div class={this.iconClass}>
				<bbg-button
					id="save-modal-dialog"
					type={buttonType}
					label={this.capitalizeFirstLetter(this.uiTextContent.general.save)}
					onClick={() => this.saveCurrentSelectionAndCloseModal()}
				>
					<i class="a-icon a-button__icon">
						<svg id="accept-icon" width="1.5em" viewBox="0 0 192 192" enable-background="new 0 0 512 512">
							<path d="M76 141.7L33.2 98.8l5.6-5.6L76 130.3l77.2-77.1 5.6 5.6z" />
						</svg>
					</i>
				</bbg-button>
			</div>
		);
	}

	private createLanguageSelector(localeList) {
		let updatedValue = this.localesSet ? "" : localeList;
		this.localesSet = true;
		return (
			<bbg-language-selector
				onLanguageSelected={localeData => this.updateLocaleFromLanguageSelector(localeData.detail)}
				languageList={updatedValue}
				defaultLanguage={this.uiTextContent.metadata.locale}
			>
				{" "}
			</bbg-language-selector>
		);
	}

	private createFooterLinks() {
		return (
			<div class="footer-links">
				{Object.values(this.uiFooterLinksData).map(link => (
					<span class="link-holder">
						<bbg-link id={link.name} target={link.linkTarget} type="simple" href={link.linkUrl}>
							{this.capitalizeFirstLetter(link.linkLabel)}
						</bbg-link>
					</span>
				))}
			</div>
		);
	}

	private createCheckboxList() {
		return this.uiCheckboxList.map(
			consentCheckbox =>
				consentCheckbox.descriptionText && (
					<p class="checkboxes">
						<bbg-checkbox key={consentCheckbox.checkboxId} fid={consentCheckbox.checkboxId} class="checkbox">
							<input style={{ display: "block" }} type="checkbox" checked={consentCheckbox.userConsents} onClick={this.handleCheckbox} />
							<label style={{ display: "grid" }}>
								<span>{consentCheckbox.label}</span>
								<p>
									{consentCheckbox.descriptionText}&nbsp;
									{consentCheckbox.linkData && consentCheckbox.linkData.linkUrl && (
										<bbg-link type="simple" target={consentCheckbox.linkData.linkTarget} href={consentCheckbox.linkData.linkUrl} class="checkbox-link">
											{this.capitalizeFirstLetter(consentCheckbox.linkData.linkLabel)}
										</bbg-link>
									)}
								</p>
							</label>
						</bbg-checkbox>
					</p>
				),
		);
	}

	private capitalizeFirstLetter(textValue: string) {
		if (textValue) {
			LogService.log.trace("capitalizeFirstLetter called with", textValue);
			if (typeof textValue !== "string") {
				LogService.log.error('"TextValue"', textValue, "is not a string!");
			}
			return textValue.charAt(0).toUpperCase() + textValue.slice(1);
		}
	}

	/**
	 * Marks all checkboxes, emits the respective 'checkboxToggleModalEvent' events, waits and emits the 'saveAcceptAllEvent' event
	 */
	private saveAcceptAllAndCloseModal() {
		const allCheckboxInputElements = this.hostElement.querySelectorAll("p.checkboxes > bbg-checkbox > div > input");

		/* eslint-disable unicorn/no-array-for-each */
		allCheckboxInputElements.forEach((checkboxElement: HTMLInputElement) => {
			checkboxElement.checked = true;

			const indexId: string = checkboxElement.getAttribute("id");
			const consent: ConsentType = { name: indexId, userConsents: checkboxElement.checked };

			this.checkboxToggleModalEvent.emit(consent);
		});
		/* eslint-enable unicorn/no-array-for-each */

		setTimeout(() => {
			this.saveAcceptAllEvent.emit();
		}, 500);
	}

	/**
	 * Unmarks all checkboxes, emits the respective 'checkboxToggleModalEvent' events, and emits the 'saveDeclineAllEvent' event
	 */
	private saveDeclineAllAndCloseModal() {
		const noCheckboxInputElements = this.hostElement.querySelectorAll("p.checkboxes >bbg-checkbox > div >input");
		/* eslint-disable unicorn/no-array-for-each */
		noCheckboxInputElements.forEach(
			(checkboxElement: HTMLInputElement) => {
				checkboxElement.checked = false;

				const indexId: string = checkboxElement.getAttribute("id");
				const consent: ConsentType = { name: indexId, userConsents: checkboxElement.checked };

				this.checkboxToggleModalEvent.emit(consent);
			} /* eslint-enable unicorn/no-array-for-each */,
		);
		this.saveDeclineAllEvent.emit();
	}

	/**
	 * Emits the 'saveCurrentSelectionEvent'
	 */
	private saveCurrentSelectionAndCloseModal() {
		this.saveCurrentSelectionEvent.emit();
	}

	private handleCheckbox(event: Event) {
		const checkboxElement: HTMLInputElement = event.target as HTMLInputElement;
		const indexId: string = checkboxElement.getAttribute("id");
		const userConsents: boolean = checkboxElement.checked;
		const consent: ConsentType = { name: indexId, userConsents };

		this.checkboxToggleModalEvent.emit(consent);
	}

	/**
	 * Emits the 'changeToEditViewEvent'
	 */
	private changeToEditView() {
		this.changeToEditViewEvent.emit();
	}

	/**
	 * Emits the 'changeToEditViewEvent'
	 */
	private changeToInitialView() {
		this.changeToInitialViewEvent.emit();
	}

	/**
	 * Emits the 'localeChangeEvent'
	 */
	private updateLocaleFromLanguageSelector(localeData) {
		LogService.log.info("Locale has been updated to: ", localeData);
		this.localeChangeEvent.emit(localeData);
	}
}
